<template>
  <div ref="crudTableFitRoot" :data-testid="getTestId('company-manager-crud-container')">
    <v-data-table
      :headers="headers"
      :items="displayCompanyManagers"
      class="elevation-1"
      :data-testid="getTestId('company-manager-data-table')"
      hide-default-footer
      hide-details="auto"
      :loading="apiLoading"
      :loading-text="$t('table.loading')"
      :custom-sort="customSort"
      :options.sync="tableOptions"
      @update:options="changedOptions"
    >
      <template slot="top">
        <v-toolbar flat class="solvit-crud-table-toolbar" :data-testid="getTestId('company-manager-toolbar')">
          <v-toolbar-title style="align-items: center; display: flex">
            <v-icon left>
              mdi-account-supervisor
            </v-icon>{{ $t('companyManagerCrud.title') }}
          </v-toolbar-title>
          <v-divider class="mx-4" inset vertical />
          <v-spacer />
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            :label="$t('companyManagerCrud.searchLabel')"
            single-line
            hide-details="auto"
            :data-testid="getTestId('company-manager-search-input')"
          />
          <v-spacer />
          <v-btn
            outlined
            class="mb-2 solvit-crud-outline-btn solvit-crud-outline-btn--primary"
            :data-testid="getTestId('company-manager-add-button')"
            @click="openAddDialog"
          >
            <v-icon size="medium">
              mdi-plus
            </v-icon>
            <span class="ml-1">{{ $t('common.addNew') }}</span>
          </v-btn>
        </v-toolbar>
      </template>

      <template slot="item.actions" slot-scope="props">
        <FixDeleteBtn
          :key="'delete-btn-' + Date.now()"
          :item="props.item"
          :data-testid="getTestId('company-manager-action-buttons')"
          @editItem="editItem"
          @deleteItem="deleteItem"
        />
      </template>

      <template slot="no-data">
        <v-btn outlined class="solvit-crud-outline-btn" :data-testid="getTestId('company-manager-refresh-button')" @click="initialize">
          {{ $t('common.refresh') }}
        </v-btn>
      </template>
    </v-data-table>

    <div class="text-center pt-2">
      <v-pagination
        v-model="page"
        :length="pageCount"
        :total-visible="10"
        next-icon="mdi-chevron-right"
        prev-icon="mdi-chevron-left"
        :data-testid="getTestId('company-manager-pagination')"
      />
    </div>

    <CompanyManagerEditDialog
      :key="addDialogKey"
      :dialog="addDialog"
      :item="defaultItem"
      :sites="sitesList"
      :companies="companiesList"
      :data-testid="getTestId('company-manager-add-dialog')"
      @close="closeAddDialog"
      @success="handleAddSuccess"
    />

    <CompanyManagerEditDialog
      :key="editDialogKey"
      :dialog="editDialog"
      :item="selectedItem"
      :sites="sitesList"
      :companies="companiesList"
      :data-testid="getTestId('company-manager-edit-dialog')"
      @close="closeEditDialog"
      @success="handleEditSuccess"
    />
  </div>
</template>

<script>
import Api from '../../apis/Api';
import FixDeleteBtn from '../button/FixDeleteBtn.vue';
import CompanyManagerEditDialog from '../../components/dialog/CompanyManagerEditDialog.vue';
import { getTestId } from '@/utils/testUtils';
import { debounce } from '@/utils/simpleDebounce';
import { getValueOrDefault, getObjectPropertyOrDefault } from '@/utils/tableUtils';
import { 
  createDefaultTableOptions, 
  buildSortParam, 
  buildPaginationParams, 
  buildApiUrl, 
  transformApiResponse 
} from '../../utils/tableUtils';
import { transformToCompanyManagerTableItems } from '../../constants/companyManagerTableItem';
import { crudTablePageSizeFitMixin } from '@/ui/mixins/crudTablePageSizeFitMixin';

export default {
  name: 'CompanyManagerCrudTable',
  mixins: [crudTablePageSizeFitMixin],
  components: {
    FixDeleteBtn,
    CompanyManagerEditDialog
  },
  data: () => ({
    valid: true,
    // tableUtils를 사용한 기본 테이블 옵션 생성 (created에서 viewport fit로 덮어씀)
    tableOptions: createDefaultTableOptions(['id'], [true], 10),
    itemsPerPage: 10,
    pageCount: 0,
    paging: true,
    page: 1,
    domain: 'company-managers',
    addDialog: false,
    addDialogKey: 'addDialogKey'+Date.now(),
    editDialog: false,
    editDialogKey: 'editDialogKey'+Date.now(),
    selectedUser: null,
    search: '',
    companyManagers: [],
    sites: [],
    sitesList: [],
    companiesList: [], // 🆕 회사 목록 추가
    editedIndex: -1,
    editedItem: {
      id: null,
      name: '',
      phone: '',
      email: '',
      siteRelations: [] // 🆕 siteSeqs → siteRelations로 변경
    },
    defaultItem: {
      name: '',
      phone: '',
      email: '',
      siteRelations: [] // 🆕 siteSeqs → siteRelations로 변경
    },
    apiLoading: true,
    selectedItem: null
  }),
  computed: {
    formTitle() {
      return this.editedIndex === -1 ? this.$t('common.addNew') : this.$t('common.edit');
    },
    headers() {
      void this.$i18n.locale;
      const c = (key) => this.$t(`companyManagerCrud.col.${key}`);
      return [
        { text: c('name'), value: 'name', sortable: true },
        { text: c('companyDisplay'), value: 'companyDisplay', sortable: true },
        { text: c('siteRelationsDisplay'), value: 'siteRelationsDisplay', sortable: false },
        { text: c('phone'), value: 'phone', sortable: true },
        { text: c('email'), value: 'email', sortable: true },
        {
          text: c('actions'),
          value: 'actions',
          sortable: false,
          align: 'center',
          width: '120px'
        }
      ];
    },

    // 🆕 테이블 표시용 데이터 (siteRelations를 포맷팅된 문자열로 변환)
    displayCompanyManagers() {
      return this.companyManagers.map(manager => ({
        ...manager,
        phone: getValueOrDefault(manager.phone),
        email: getValueOrDefault(manager.email),
        companyDisplay: this.formatCompanyDisplay(manager.company), // 🆕 회사 정보 포맷팅
        siteRelationsDisplay: this.formatSiteRelations(manager.siteRelations)
      }));
    }
  },
  watch: {
    search(val) {
      if (this.paging) {
        // 검색어 변경 시에만 debounce 적용
        this.debouncedSearch(val, 1, this.tableOptions.sortBy, this.tableOptions.sortDesc);
      }
    },
    page(val) {
      if (this.isCrudTableFitProgrammaticUpdate()) {
        return;
      }
      if (this.paging) {
        // 페이지 변경은 즉시 실행
        this.searchCompanyManagerData(this.search, val, this.tableOptions.sortBy, this.tableOptions.sortDesc);
      }
    }
  },
  created() {
    // 초기 정렬 옵션을 명시적으로 설정
    this.tableOptions.sortBy = ['id'];
    this.tableOptions.sortDesc = [true];
    
    // debouncedSearch 초기화
    this.initialize();
    
    // changedOptions에서 자동으로 초기 데이터 로딩이 진행되므로 별도 호출 불필요
  },
  methods: {
    getTestId(testId) {
      return getTestId(testId);
    },

    /**
     * 기존의 sort기능을 사용하지 않도록 처리
     */
    customSort(items, _index, _isDesc) {
      return items;
    },

    changedOptions(val) {
      
      // tableOptions는 이미 자동으로 업데이트됨
      // 별도로 변경할 필요 없음
      
      // 초기 로드 시에도 데이터를 가져오기 위해 searchCompanyManagerData 호출
      this.searchCompanyManagerData(
        this.search, 
        this.page, 
        this.tableOptions.sortBy, 
        this.tableOptions.sortDesc
      );
    },

    /**
     * 담당자 데이터 조회
     */
    // Promise.all을 사용하여 병렬로 API 호출
    async searchCompanyManagerData(keyword, page, sortBy, sortDesc) {
      const request = this.beginCrudTableRequest();
      this.apiLoading = true;

      try {
        // 정렬 파라미터 생성 - ProjectCrudTable과 동일한 방식
        let sortParam = '';
        if (sortBy && sortBy.length > 0) {
          const sortableFields = {
            'name': 'name',
            'companyDisplay': 'company.companyName',
            'phone': 'phone',
            'email': 'email',
            'id': 'id'
          };
          
          const sortFields = sortBy.map((field, index) => {
            const direction = (sortDesc && sortDesc[index]) ? 'desc' : 'asc';
            const apiField = sortableFields[field] || field;
            return `${apiField},${direction}`;
          });
          sortParam = sortFields.join(',');
        } else {
          // 기본값: id,desc
          sortParam = 'id,desc';
        }
        
        // 페이지 번호는 1부터 시작하도록 조정
        const currentPage = page || this.page;
        const adjustedPage = currentPage - 1; // API는 0부터 시작
        
        console.log("sortBy:", sortBy);
        console.log("sortDesc:", sortDesc);

        // Promise.all로 병렬 실행 - 가독성 개선: API 호출 바로 위에서 params 구성
        const managerApiCall = Api.getApiResource(`/${this.domain}`, {
          size: this.itemsPerPage,
          page: adjustedPage,
          sort: sortParam,
          ...(keyword && { keyword })
        }, { signal: request.signal }).catch((error) => {
          if (this.isCrudTableRequestCurrent(request.token)) {
            console.error('담당자 데이터 조회 중 오류 발생:', error);
          }
          return {
            data: {
              content: {
                page: { totalElements: 0, totalPages: 0, number: 0 },
                content: []
              }
            }
          };
        });

        const siteApiCall = Api.getApiResource('/sites/list', {}, { signal: request.signal }).catch(() => {
          return { data: { content: [] } };
        });

        const companyApiCall = Api.getApiResource('/companies/list', {}, { signal: request.signal }).catch(() => {
          return { data: { content: [] } };
        });

        // Promise.all로 병렬 실행
        const [managerRes, siteRes, companyRes] = await Promise.all([
          managerApiCall,
          siteApiCall,
          companyApiCall
        ]);
        if (!this.isCrudTableRequestCurrent(request.token)) return;

        // 담당자 데이터 처리
        const transformedData = transformApiResponse(managerRes.data, (items) => {
          return transformToCompanyManagerTableItems(items);
        });
        
        this.companyManagers = transformedData.items;
        this.pageCount = transformedData.pageCount;
        this.page = transformedData.page;

        // 사이트 데이터 처리
        const siteContent = siteRes?.data?.content;
        if (Array.isArray(siteContent)) {
          this.sitesList = siteContent.map(site => ({
            id: site.id,
            name: site.name,
            siteRelations: site.siteRelations || []
          }));
        } else {
          console.warn('사이트 데이터가 배열이 아님:', siteContent);
          this.sitesList = [];
        }
        this.sites = this.sitesList;

        // 회사 데이터 처리
        const companyContent = companyRes?.data?.content;
        console.log('companyContent', companyContent);
        if (Array.isArray(companyContent)) {
          this.companiesList = companyContent;
        } else {
          console.warn('회사 데이터가 배열이 아님:', companyContent);
          this.companiesList = [];
        }

        console.log('담당자 데이터:', this.companyManagers);
        console.log('사이트 데이터:', this.sitesList);
        console.log('회사 데이터:', this.companiesList);

      } catch (error) {
        if (!this.isCrudTableRequestCurrent(request.token)) return;
        console.error('데이터 조회 중 오류 발생:', error);
        this.companyManagers = [];
        this.sitesList = [];
        this.companiesList = [];
      } finally {
        if (this.isCrudTableRequestCurrent(request.token)) {
          this.apiLoading = false;
          this.paging = true;
          this.finishCrudTableRequest(request.token);
        }
      }
    },

    initialize() {
      this.debouncedSearch = debounce(this.searchCompanyManagerData, 200);
    },

    editItem(item) {
      console.log('편집할 담당자:', item);
      // 🆕 옵셔널 체이닝 사용
      if (item?.id) {
        this.selectedItem = {
          id: item.id,
          name: item.name ?? '',
          phone: item.phone ?? '',
          email: item.email ?? '',
          company: item.company ?? null,
          siteRelations: item.siteRelations ?? [] // 🆕 siteSeqs → siteRelations로 변경
        };
        this.editDialog = true;
        this.editDialogKey = 'editDialogKey'+Date.now();
        console.log('설정된 selectedItem:', this.selectedItem);
      } else {
        console.error('편집할 담당자 정보가 유효하지 않습니다:', item);
        alert(this.$t('companyManagerCrud.editLoadError'));
      }
    },

    deleteItem(item) {
      // 🆕 안전한 삭제를 위해 ID로 확인
      if (!item || !item.id) {
        console.error('삭제할 담당자 정보가 유효하지 않습니다:', item);
        alert(this.$t('companyManagerCrud.deleteLoadError'));
        return;
      }

      confirm(this.$t('common.confirmDeleteSelected')) &&
        Api.delete(`/${this.domain}/${item.id}`).then(() => {
          // 🆕 삭제 성공 시 API 재요청으로 데이터 동기화
          this.searchCompanyManagerData(this.search, this.page, this.tableOptions.sortBy, this.tableOptions.sortDesc);
        }).catch((error) => {
          console.error('담당자 삭제 중 오류 발생:', error);
          // 삭제 실패 시 사용자에게 알림
          alert(this.$t('companyManagerCrud.deleteApiError'));
        });
    },

    closeEditDialog() {
      this.editDialog = false;
      this.selectedItem = null;
    },

    handleEditSuccess() {
      this.closeEditDialog();
      this.paging = true;
      // 🆕 현재 페이지 데이터만 새로고침 (더 효율적)
      this.searchCompanyManagerData(this.search, this.page, this.tableOptions.sortBy, this.tableOptions.sortDesc);
    },

    openAddDialog() {
      // 🆕 기본값으로 defaultItem 설정
      this.defaultItem = {
        id: null,
        name: '',
        phone: '',
        email: '',
        siteRelations: [] // 🆕 siteSeqs → siteRelations로 변경
      };
      this.addDialog = true;
      this.addDialogKey = 'addDialogKey'+Date.now();
      console.log('추가 모드 - defaultItem 설정:', this.defaultItem);
    },

    closeAddDialog() {
      this.addDialog = false;
    },

    handleAddSuccess() {
      this.closeAddDialog();
      this.paging = true;
      // 🆕 현재 페이지 데이터만 새로고침 (더 효율적)
      this.searchCompanyManagerData(this.search, this.page, this.tableOptions.sortBy, this.tableOptions.sortDesc);
    },

    // 🆕 siteRelations에서 사이트명을 추출하여 포맷팅 (3개 이상이면 "외 N개" 처리)
    formatSiteRelations(siteRelations) {
      if (!siteRelations || !Array.isArray(siteRelations) || siteRelations.length === 0) {
        return '-';
      }

      // siteRelations에서 siteName 추출
      const siteNames = siteRelations
        .filter(relation => relation.siteName)
        .map(relation => relation.siteName);

      if (siteNames.length === 0) {
        return '-';
      }

      // 개수가 많을 때만 "외 N개" 처리
      if (siteNames.length > 3) {
        return this.$t('companyManagerCrud.siteListMore', {
          base: siteNames.slice(0, 3).join(', '),
          count: siteNames.length - 3
        });
      }

      return siteNames.join(', ');
    },

    // 🆕 회사 정보 포맷팅
    formatCompanyDisplay(company) {
      return getObjectPropertyOrDefault(company, 'companyName');
    }
  }
};
</script>

<style scoped>
.v-dialog__content {
  max-height: 100%;
}
.v-dialog:not(.v-dialog--fullscreen) {
  max-height: 100%;
}
.custom-padding {
  padding: 8px 12px !important;
}

.required::after {
  content: "*";
  color: red;
  margin-left: 4px;
}

.v-data-table >>> th {
  font-weight: 700 !important;
}
</style>
